import { Component, OnInit } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { JsaService } from '../../../../services/jsa/jsa.service';
import { GoodCatchService } from '../../../../services/goodCatch/goodCatch.service';
import { NearMissService } from '../../../../services/nearMiss/nearMiss.service';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonFunctionsService } from '../../../../services/common-functions.service';

interface OfficeOption {
  value: string;
  label: string;
}

const ALL_OFFICES = '__all__';
const OFFICE_NOT_SPECIFIED = 'Office not Specified';

@Component({
    selector: 'app-safety-analytics',
    imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatToolbarModule,
    MatButton,
    RouterModule,
    MatIconModule,
    BaseChartDirective,
    MatTabsModule,
    MatDividerModule,
    MatProgressSpinnerModule
],
    templateUrl: './safety-analytics.component.html',
    styleUrl: './safety-analytics.component.css'
})
export class SafetyAnalyticsComponent implements OnInit {
  readonly ALL_OFFICES = ALL_OFFICES;

  // Currently-active offices are listed first; archived offices are grouped at the bottom
  // so historical data is still selectable without cluttering the common choices.
  readonly openOffices: OfficeOption[] = [
    { value: 'houston', label: 'Houston' },
    { value: 'ftworth', label: 'Ft. Worth' },
    { value: 'irving', label: 'Irving' },
    { value: 'windsor', label: 'Windsor' },
  ];
  readonly archivedOffices: OfficeOption[] = [
    { value: 'austin', label: 'Austin' },
    { value: 'bossiercity', label: 'Bossier City' },
    { value: 'frisco', label: 'Frisco' },
    { value: 'magnolia', label: 'Magnolia' },
    { value: 'midland', label: 'Midland' },
  ];

  // Legend/line ordering for the per-month charts.
  private readonly officeOrder = [
    'Houston', 'Ft. Worth', 'Irving', 'Windsor',
    'Austin', 'Bossier City', 'Frisco', 'Magnolia', 'Midland',
    OFFICE_NOT_SPECIFIED,
  ];

  form: FormGroup;
  private allWasSelected = true;

  loading = false;
  hasFetched = false;

  // Overview scorecards
  totalJsa = 0;
  totalGoodCatch = 0;
  totalNearMiss = 0;

  // Applied-filter summary shown to the user
  selectedOfficeLabel = 'All Offices';
  selectedRangeLabel = 'Last 6 Months';

  chartData: ChartData<'line'> = { labels: [], datasets: [] };
  goodCatchChartData: ChartData<'line'> = { labels: [], datasets: [] };
  nearMissChartData: ChartData<'line'> = { labels: [], datasets: [] };
  hazardChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  equipmentChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { title: { display: true, text: 'Month' } },
      y: {
        title: { display: true, text: 'Total Submissions' },
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };
  hazardChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: { title: { display: true, text: 'Occurrences' }, beginAtZero: true },
      y: { title: { display: true, text: 'Hazards' } },
    },
  };
  equipmentChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: { title: { display: true, text: 'Count' }, beginAtZero: true },
      y: { title: { display: true, text: 'Equipment Used' } },
    },
  };

  constructor(
    private fb: FormBuilder,
    private jsaService: JsaService,
    private goodCatchService: GoodCatchService,
    private nearMissService: NearMissService,
    private commonFuncs: CommonFunctionsService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      office: [[ALL_OFFICES]],
      startDate: [null],
      endDate: [null],
    });
    this.fetchAnalytics();
  }

  // Keeps the "All Offices" pseudo-option and specific offices mutually exclusive.
  onOfficeSelectionChange(): void {
    const value: string[] = this.form.value.office || [];
    const hasAll = value.includes(ALL_OFFICES);
    if (hasAll && value.length > 1) {
      // If "All" was already selected, the user just added a specific office → drop "All".
      // Otherwise the user just picked "All" → collapse to only "All".
      const next = this.allWasSelected ? value.filter((v) => v !== ALL_OFFICES) : [ALL_OFFICES];
      this.form.get('office')?.setValue(next.length ? next : [ALL_OFFICES]);
    } else if (value.length === 0) {
      this.form.get('office')?.setValue([ALL_OFFICES]);
    }
    this.allWasSelected = (this.form.value.office || []).includes(ALL_OFFICES);
  }

  private resolvedOffices(): string[] {
    const value: string[] = this.form.value.office || [];
    return value.includes(ALL_OFFICES) ? [] : value;
  }

  fetchAnalytics(): void {
    const { startDate, endDate } = this.form.value;
    const offices = this.resolvedOffices();
    const officeArg = offices.length ? offices : undefined;
    const startArg = startDate ? this.commonFuncs.convertToUtc(startDate) : undefined;
    const endArg = endDate ? this.commonFuncs.convertToUtcEod(endDate) : undefined;

    this.selectedOfficeLabel = this.buildOfficeLabel(offices);
    this.selectedRangeLabel = this.buildRangeLabel(startDate, endDate);
    this.loading = true;

    this.jsaService.getJsaAnalytics(officeArg, startArg, endArg).subscribe({
      next: (res) => {
        this.processHazardData(res?.data?.hazards || {});
        this.processEquipmentData(res?.data?.equipmentUsed || {});
      },
      error: (err) => console.error('Error fetching JSA analytics:', err),
    });

    this.jsaService.getJsaTotalsByMonth(officeArg, startArg, endArg).subscribe({
      next: (res) => {
        const totals = res?.data || [];
        this.chartData = this.buildMonthlyLineChart(totals);
        this.totalJsa = this.sumTotals(totals);
        this.loading = false;
        this.hasFetched = true;
      },
      error: (err) => {
        console.error('Error fetching JSA totals:', err);
        this.loading = false;
      },
    });

    this.goodCatchService.getGoodCatchTotalsByMonth(officeArg, startArg, endArg).subscribe({
      next: (res) => {
        const totals = res?.data || [];
        this.goodCatchChartData = this.buildMonthlyLineChart(totals);
        this.totalGoodCatch = this.sumTotals(totals);
      },
      error: (err) => console.error('Error fetching Good Catch totals:', err),
    });

    this.nearMissService.getNearMissTotalsByMonth(officeArg, startArg, endArg).subscribe({
      next: (res) => {
        const totals = res?.data || [];
        this.nearMissChartData = this.buildMonthlyLineChart(totals);
        this.totalNearMiss = this.sumTotals(totals);
      },
      error: (err) => console.error('Error fetching Near Miss totals:', err),
    });
  }

  resetData(): void {
    this.form.reset({ office: [ALL_OFFICES], startDate: null, endDate: null });
    this.allWasSelected = true;
    this.fetchAnalytics();
  }

  private buildOfficeLabel(offices: string[]): string {
    if (!offices.length) return 'All Offices';
    const labels = [...this.openOffices, ...this.archivedOffices]
      .filter((o) => offices.includes(o.value))
      .map((o) => o.label);
    return labels.join(', ');
  }

  private buildRangeLabel(startDate: string, endDate: string): string {
    if (!startDate && !endDate) return 'Last 6 Months';
    const start = startDate ? this.formatShortDate(startDate) : 'All Time';
    const end = endDate ? this.formatShortDate(endDate) : 'Present';
    return `${start} - ${end}`;
  }

  private sumTotals(totals: any[]): number {
    return (totals || []).reduce(
      (sum, entry) => sum + (entry.monthlyData || []).reduce((s: number, m: any) => s + (m.totalSubmissions || 0), 0),
      0,
    );
  }

  formatShortDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  processHazardData(hazards: Record<string, number>): void {
    const hazardLabelMap: Record<string, string> = {
      drivingTravel: 'Driving/Travel',
      confinedSpaces: 'Confined Spaces',
      waterHazard: 'Water Hazard',
      sharpEdges: 'Sharp Edges/Hot Surfaces',
      heatColdExposure: 'Heat/Cold Exposure',
      liftingHazards: 'Lifting Hazards',
      fireExplosionHazard: 'Fire/Explosion',
      pinchCrushHazards: 'Pinch/Crush/Striking',
      electricalShock: 'Electrical Shock',
      loneWorkers: 'Lone Workers',
      excavationHazard: 'Excavation Hazard',
      hazardousChemicalExposure: 'Chemical Exposure',
      roughTerrain: 'Rough Terrain',
      potentialEnergyRelease: 'Potential Energy Release',
      highNoiseLevels: 'High Noise Levels',
      wildlifeContact: 'Wildlife/Livestock',
      movingEquipment: 'Moving Equipment',
      fallPotential: 'Fall Potential',
      suspendedLoads: 'Suspended Loads',
      poisonousPlants: 'Poisonous Plants',
    };

    this.hazardChartData = {
      labels: Object.keys(hazards).map((hazard) => hazardLabelMap[hazard] || hazard),
      datasets: [
        {
          label: 'Hazard Occurrences',
          data: Object.values(hazards),
          backgroundColor: '#FF6384',
          borderColor: '#CC143C',
          borderWidth: 1,
        },
      ],
    };
  }

  processEquipmentData(equipment: Record<string, number>): void {
    const equipmentLabelMap: Record<string, string> = {
      gpsUnit: 'GPS Unit',
      totalStation: 'Total Station',
      lineLocator: 'Line Locator',
      sx10OrSx12: 'SX-10 or SX-12',
      noneOfTheAbove: 'None of the Above',
    };

    this.equipmentChartData = {
      labels: Object.keys(equipment).map((item) => equipmentLabelMap[item] || item),
      datasets: [
        {
          label: 'Equipment Used',
          data: Object.values(equipment),
          backgroundColor: '#4BC0C0',
          borderColor: '#339999',
          borderWidth: 1,
        },
      ],
    };
  }

  // Shared builder for the JSA / Good Catch / Near Miss per-month line charts.
  // Only offices with at least one submission in the window get a line (and a legend
  // entry), so offices with no data in the selected window don't clutter the key.
  private buildMonthlyLineChart(totals: any[]): ChartData<'line'> {
    const allMonths = new Set<string>();
    const officeMap = new Map<string, Map<string, number>>();

    (totals || []).forEach((entry) => {
      const office = this.formatOfficeName(entry.office);
      if (!officeMap.has(office)) officeMap.set(office, new Map());
      (entry.monthlyData || []).forEach(({ month, totalSubmissions }: any) => {
        const formattedMonth = this.formatMonthWithYear(month);
        allMonths.add(formattedMonth);
        officeMap.get(office)!.set(formattedMonth, totalSubmissions);
      });
    });

    const sortedMonths = Array.from(allMonths).sort(
      (a, b) => this.getFullMonthIndex(a) - this.getFullMonthIndex(b),
    );

    const presentOffices = Array.from(officeMap.keys())
      .filter((office) => Array.from(officeMap.get(office)!.values()).some((v) => v > 0))
      .sort((a, b) => this.officeSortIndex(a) - this.officeSortIndex(b));

    const datasets = presentOffices.map((office) => {
      const monthMap = officeMap.get(office)!;
      return {
        label: office,
        data: sortedMonths.map((month) => monthMap.get(month) || 0),
        borderWidth: 2,
        borderColor: this.getOfficeBorderColor(office),
        backgroundColor: this.getOfficeColor(office),
        fill: false,
      };
    });

    return { labels: sortedMonths, datasets };
  }

  private officeSortIndex(office: string): number {
    const idx = this.officeOrder.indexOf(office);
    return idx === -1 ? this.officeOrder.length : idx;
  }

  getOfficeColor(office: string): string {
    const officeColors: Record<string, string> = {
      Houston: '#9AD0F5',
      'Ft. Worth': '#FFB1C1',
      Irving: '#FFCF9F',
      Midland: '#FFE6AA',
      Windsor: '#A5DFDF',
      Austin: '#CCB2FF',
      Frisco: '#E4E5E7',
      'Bossier City': '#E2FABE',
      Magnolia: '#D7BFA8',
      [OFFICE_NOT_SPECIFIED]: '#E0E0E0',
    };
    return officeColors[office] || '#000000';
  }

  getOfficeBorderColor(office: string): string {
    const officeColors: Record<string, string> = {
      Houston: '#36A2EB',
      'Ft. Worth': '#FF6384',
      Irving: '#FF9F40',
      Midland: '#FFCD56',
      Windsor: '#4BC0C0',
      Austin: '#9966FF',
      Frisco: '#C9CBCF',
      'Bossier City': '#D0FF89',
      Magnolia: '#A9744F',
      [OFFICE_NOT_SPECIFIED]: '#9E9E9E',
    };
    return officeColors[office] || '#000000';
  }

  formatOfficeName(office: string): string {
    const officeMap: Record<string, string> = {
      houston: 'Houston',
      ftworth: 'Ft. Worth',
      irving: 'Irving',
      frisco: 'Frisco',
      bossiercity: 'Bossier City',
      austin: 'Austin',
      midland: 'Midland',
      magnolia: 'Magnolia',
      windsor: 'Windsor',
    };
    return officeMap[(office || '').toLowerCase()] || office;
  }

  formatMonthWithYear(isoMonth: string): string {
    const [year, month] = isoMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleString('en-US', { month: 'short' }) + ` ${year.toString().slice(-2)}`;
  }

  getFullMonthIndex(monthYear: string): number {
    const [monthStr, yearStr] = monthYear.split(' ');
    const year = parseInt('20' + yearStr, 10);
    return year * 12 + this.getMonthIndex(monthStr);
  }

  getMonthIndex(shortMonth: string): number {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.indexOf(shortMonth);
  }
}
